/**
 * Reads the pixel dimensions of a style's images by loading them, so the size recorded for an
 * enforced style describes the assets that actually exist instead of a number typed from memory.
 *
 * Loading the image measures the picture the players' lobby is served, delivery-side resizing
 * included. Intrinsic dimensions are readable across origins without CORS, unlike the pixel data,
 * so the asset host needs to cooperate no further than serving the image. The browser has also
 * already applied any EXIF rotation to them, so a portrait asset stored as landscape reads in the
 * orientation the player sees.
 */
import { AssetDimensions, AssetSample, resolveAssetUrl } from "./assetSample";

/** What one sample measured. No `dimensions` means nothing usable could be read from that image. */
export interface AssetSampleMeasurement {
  sample: AssetSample;
  url: string;
  dimensions?: AssetDimensions;
}

/** Disagreement is reported rather than resolved: only the caller knows whether a minority reading is a stray asset or a sign the template is wrong. */
export type AssetMeasurementOutcome =
  | { status: "blocked"; samples: AssetSampleMeasurement[] }
  | { status: "agreed"; dimensions: AssetDimensions; samples: AssetSampleMeasurement[] }
  | { status: "disagreed"; majority: AssetDimensions; samples: AssetSampleMeasurement[] };

/** Makes the element one sample is loaded into, so a caller can measure without a live document. */
type ImageFactory = () => HTMLImageElement;

/**
 * A request the browser neither completes nor fails — an asset host that accepts the connection and
 * then goes quiet — would otherwise leave its sample pending for good, and with it the whole
 * outcome and the form waiting on it.
 */
const READING_DEADLINE_MS = 15000;

/**
 * An image that failed to decode reports zero, and a zero height would give the preview an impossible
 * ratio. The property is read as `unknown` because an element handed over by a caller carries no
 * guarantee about it, and `Number.isFinite` turns away the `Infinity` the positivity check admits.
 */
function readDimension(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) && value > 0 ? value : undefined;
}

function readDimensions(image: HTMLImageElement): AssetDimensions | undefined {
  const width = readDimension(image.naturalWidth);
  const height = readDimension(image.naturalHeight);

  return width === undefined || height === undefined ? undefined : { width, height };
}

function measureSample(template: string, sample: AssetSample, createImage: ImageFactory): Promise<AssetSampleMeasurement> {
  const url = resolveAssetUrl(template, sample);

  return new Promise((resolve) => {
    const image = createImage();
    let deadline: ReturnType<typeof setTimeout> | undefined;

    const settle = (measurement: AssetSampleMeasurement): void => {
      clearTimeout(deadline);
      resolve(measurement);
    };

    deadline = setTimeout(() => settle({ sample, url }), READING_DEADLINE_MS);
    image.addEventListener("load", () => settle({ sample, url, dimensions: readDimensions(image) }));
    image.addEventListener("error", () => settle({ sample, url }));
    // Assigned last, so neither event can fire before there is anything listening for it.
    image.src = url;
  });
}

function toKey(dimensions: AssetDimensions): string {
  return `${dimensions.width}x${dimensions.height}`;
}

interface DimensionTally {
  dimensions: AssetDimensions;
  count: number;
}

/**
 * A `Map` keeps its keys in insertion order and only a strictly higher count displaces the leader,
 * so ties keep the first reading and measuring the same samples again never flips the answer.
 */
function findMajority(dimensions: AssetDimensions[]): AssetDimensions | undefined {
  const tallies = new Map<string, DimensionTally>();
  dimensions.forEach((entry) => {
    const key = toKey(entry);
    const tally = tallies.get(key);
    if (tally === undefined) {
      tallies.set(key, { dimensions: entry, count: 1 });
    } else {
      tally.count += 1;
    }
  });

  const leader = Array.from(tallies.values()).reduce<DimensionTally | undefined>(
    (best, tally) => (best === undefined || tally.count > best.count ? tally : best),
    undefined,
  );
  return leader?.dimensions;
}

/**
 * Nothing here throws: an image the browser cannot load is simply a sample that could not be read,
 * and one missing asset must not take down the form the operator is editing.
 */
export async function measureAssetSize(
  template: string,
  samples: AssetSample[],
  createImage: ImageFactory = () => new Image(),
): Promise<AssetMeasurementOutcome> {
  const results = await Promise.all(samples.map((sample) => measureSample(template, sample, createImage)));

  const measured = results.map((result) => result.dimensions).filter((dimensions): dimensions is AssetDimensions => dimensions !== undefined);

  const majority = findMajority(measured);
  if (majority === undefined) {
    return { status: "blocked", samples: results };
  }

  const distinct = new Set(measured.map(toKey));
  return distinct.size === 1 ? { status: "agreed", dimensions: majority, samples: results } : { status: "disagreed", majority, samples: results };
}
